import React from 'react';
import { MapPin, Building2, Navigation, Tag } from 'lucide-react';
import { SectionCard, TabLoading, TabError, TabEmpty, StatusBadge } from './shared';
import { useUserAddresses } from '../../hooks/useUserTabs';
import { cn, formatDateTime } from '../../utils/helpers';
import type { UserAddressItem } from '../../types/userTabs';

interface AddressesTabProps {
  userId: number;
}

const AddressesTab: React.FC<AddressesTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserAddresses(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No address data available" icon={MapPin} />;

  const { items, total, businessAddress } = data;

  if (items.length === 0 && !businessAddress) {
    return <TabEmpty message="No addresses found" icon={MapPin} />;
  }

  // Filter out the business address from items if it's also in the items array
  const personalAddresses = businessAddress
    ? items.filter((item) => item.id !== businessAddress.id)
    : items;

  return (
    <div className="space-y-5">
      {/* Business Address */}
      {businessAddress && (
        <SectionCard
          title="Business Address"
          icon={Building2}
          actions={<StatusBadge status="Business" variant="info" />}
        >
          <AddressCard address={businessAddress} featured />
        </SectionCard>
      )}

      {/* All Addresses */}
      {personalAddresses.length > 0 && (
        <SectionCard
          title="Addresses"
          icon={MapPin}
          subtitle={`${total} address${total !== 1 ? 'es' : ''} on file`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {personalAddresses.map((address) => (
              <AddressCard key={address.id} address={address} />
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// ============================================================================
// Address Card Component
// ============================================================================

interface AddressCardProps {
  address: UserAddressItem;
  featured?: boolean;
}

const AddressCard: React.FC<AddressCardProps> = ({ address, featured = false }) => {
  const addressParts = [address.address, address.city, address.state, address.zipCode]
    .filter(Boolean);
  const fullAddress = addressParts.join(', ');
  const hasCoordinates = address.latitude != null && address.longitude != null;

  return (
    <div
      className={cn(
        'rounded-lg border bg-muted/5 p-4',
        featured
          ? 'border-blue-500/30 bg-blue-500/5'
          : 'border-border/50'
      )}
    >
      {/* Label */}
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <div className={cn(
            'p-1.5 rounded-lg',
            featured ? 'bg-blue-500/10' : 'bg-muted/30'
          )}>
            {featured ? (
              <Building2 className="h-3.5 w-3.5 text-blue-400" />
            ) : (
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
          {address.label ? (
            <span className={cn(
              'text-sm font-medium',
              featured ? 'text-blue-400' : 'text-foreground'
            )}>
              {address.label}
            </span>
          ) : (
            <span className="text-sm font-medium text-muted-foreground">
              Unlabeled Address
            </span>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
          #{address.id}
        </span>
      </div>

      {/* Full Address */}
      {fullAddress ? (
        <p className="text-sm text-foreground leading-relaxed">
          {address.address && (
            <span className="block">{address.address}</span>
          )}
          <span className="block text-muted-foreground text-xs mt-0.5">
            {[address.city, address.state, address.zipCode].filter(Boolean).join(', ')}
            {address.country && `, ${address.country}`}
          </span>
        </p>
      ) : (
        <p className="text-sm text-muted-foreground/50">No address details</p>
      )}

      {/* Coordinates and Meta */}
      <div className="mt-3 pt-2.5 border-t border-border/30 flex items-center justify-between gap-4">
        {hasCoordinates ? (
          <div className="flex items-center gap-1.5">
            <Navigation className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground font-mono">
              {address.latitude!.toFixed(6)}, {address.longitude!.toFixed(6)}
            </span>
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground/40">No coordinates</span>
        )}
        {address.createdAt && (
          <span className="text-[10px] text-muted-foreground flex-shrink-0">
            Added {formatDateTime(address.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
};

export default AddressesTab;
